<section
  *ngIf="canManage"
  class="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white"
  aria-label="Payment gateway routing rules"
>
  <div class="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-start sm:justify-between">
    <div class="flex min-w-0 gap-3">
      <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
        <vs-icon name="git-branch-02"></vs-icon>
      </div>
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-gray-900">New sale routing rules</h3>
        <p class="mt-0.5 max-w-3xl text-sm text-gray-600">
          Route new sales by policy or distributor. Rules are evaluated in the priority shown below.
        </p>
      </div>
    </div>
    <button
      vs-button
      type="button"
      theme="primary"
      class="shrink-0"
      [disabled]="loading || !activeGateways.length"
      (click)="openCreate()"
    >
      <vs-icon name="plus" slot="start"></vs-icon>
      Add rule
    </button>
  </div>

  <div *ngIf="loading" class="flex items-center justify-center gap-2 py-10 text-sm text-gray-600">
    <vs-icon name="refresh-cw-02" class="animate-spin"></vs-icon>
    Loading routing rules...
  </div>

  <div *ngIf="!loading && error" class="m-4 rounded-lg border border-danger-200 bg-danger-50 p-4">
    <p class="text-sm font-semibold text-gray-900">Routing rules could not be loaded</p>
    <p class="mt-1 text-sm text-gray-700">{{ error }}</p>
    <button vs-button type="button" appearance="outline" class="mt-3" (click)="refresh()">Try again</button>
  </div>

  <div *ngIf="!loading && !error && !rules.length" class="flex flex-col items-center gap-2 px-6 py-10 text-center">
    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <vs-icon name="git-branch-02"></vs-icon>
    </div>
    <p class="text-sm font-semibold text-gray-900">No routing rules yet</p>
    <p class="max-w-md text-sm text-gray-600">
      New sales use {{ defaultGateway?.name || 'the tenant default gateway' }} until a policy or distributor rule is
      added.
    </p>
  </div>

  <vs-table *ngIf="!loading && !error && rules.length" [columns]="columns" [data]="rules"></vs-table>

  <div
    *ngIf="!loading && !error && defaultGateway"
    class="flex items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm"
  >
    <vs-icon name="corner-down-right" class="text-gray-500"></vs-icon>
    <span class="text-gray-600">No matching rule</span>
    <span class="text-gray-400">→</span>
    <span class="font-medium text-gray-900">{{ defaultGateway.name }} (tenant default)</span>
  </div>
</section>

<vs-dialog
  #ruleDialog
  [config]="{
    dialogContainerClass: 'w-[350px] sm:w-[560px] max-w-[620px]',
    disableBackdropDismiss: saving,
  }"
>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: saving,
      icon: 'git-branch-02',
      iconTheme: 'primary',
      title: editingId ? 'Edit routing rule' : 'Add routing rule',
      subtitle: 'Choose what the sale must match and which active gateway should process it.',
      showDivider: true,
    }"
  ></vs-dialog-header>

  <form *ngIf="canManage" class="flex flex-col gap-5 pt-6" [formGroup]="form" (ngSubmit)="save()">
    <label class="flex items-center gap-2 text-sm">
      <input vsInput type="checkbox" formControlName="enabled" />
      Enable this routing rule for new sales
    </label>
    <vs-form-field
      appearance="responsive"
      [config]="{ label: 'Match by', description: 'More specific rules take priority over broader rules.' }"
    >
      <vs-select vsInput formControlName="scopeType" placeholder="Choose a match type">
        <li vs-select-option [value]="scope.POLICY">Policy</li>
        <li vs-select-option [value]="scope.DISTRIBUTOR">Distributor</li>
        <li vs-select-option [value]="scope.POLICY_DISTRIBUTOR">Policy + distributor</li>
      </vs-select>
    </vs-form-field>

    <vs-form-field *ngIf="needsPolicy" appearance="responsive" [config]="{ label: 'Policy' }">
      <vs-select vsInput formControlName="policyId" placeholder="Choose a policy">
        <li vs-select-option *ngFor="let policy of policies" [value]="policy.id">
          {{ policyLabel(policy) }}
        </li>
      </vs-select>
    </vs-form-field>

    <vs-form-field *ngIf="needsDistributor" appearance="responsive" [config]="{ label: 'Distributor' }">
      <vs-select vsInput formControlName="distributorId" placeholder="Choose a distributor">
        <li vs-select-option *ngFor="let distributor of distributors" [value]="distributor.id">
          {{ distributor.name }}
        </li>
      </vs-select>
    </vs-form-field>

    <vs-form-field
      appearance="responsive"
      [config]="{ label: 'Route payments to', description: 'Enabled rules require a gateway available for new sales.' }"
    >
      <vs-select vsInput formControlName="gatewayInstanceId" placeholder="Choose a gateway">
        <li vs-select-option *ngFor="let gateway of selectableGateways" [value]="gateway.id">
          {{ gateway.name }}{{ gateway.isDefault ? ' (tenant default)' : '' }}
        </li>
      </vs-select>
    </vs-form-field>
  </form>

  <div slot="buttons" class="flex flex-col-reverse items-center gap-3 pt-6 sm:flex-row sm:justify-end">
    <button
      vs-button
      type="button"
      class="w-full sm:w-auto"
      appearance="outline"
      [disabled]="saving"
      (click)="closeEditor()"
    >
      Cancel
    </button>
    <button vs-button type="button" class="w-full sm:w-auto" theme="primary" [disabled]="!canSave" (click)="save()">
      {{ saving ? 'Saving...' : 'Save rule' }}
      <vs-icon *ngIf="saving" name="refresh-cw-02" class="animate-spin"></vs-icon>
    </button>
  </div>
</vs-dialog>

<vs-dialog
  #deleteDialog
  [config]="{
    dialogContainerClass: 'w-[350px] sm:w-[480px] max-w-[520px]',
    disableBackdropDismiss: deleting,
  }"
>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: deleting,
      icon: 'trash-01',
      iconTheme: 'danger',
      title: 'Delete routing rule?',
      subtitle: 'Future sales will fall through to the next matching rule or the tenant default.',
      showDivider: true,
    }"
  ></vs-dialog-header>

  <div *ngIf="selectedRule" class="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
    <p class="font-medium text-gray-900">{{ criteriaLabel(selectedRule) }}</p>
    <p class="mt-1 text-gray-600">Currently routes to {{ gatewayName(selectedRule.gatewayInstanceId) }}.</p>
  </div>

  <div slot="buttons" class="flex flex-col-reverse items-center gap-3 pt-6 sm:flex-row sm:justify-end">
    <button
      vs-button
      type="button"
      class="w-full sm:w-auto"
      appearance="outline"
      [disabled]="deleting"
      (click)="closeDelete()"
    >
      Cancel
    </button>
    <button
      vs-button
      type="button"
      class="w-full sm:w-auto"
      theme="danger"
      [disabled]="!canManage || deleting"
      (click)="deleteRule()"
    >
      {{ deleting ? 'Deleting...' : 'Delete rule' }}
      <vs-icon *ngIf="deleting" name="refresh-cw-02" class="animate-spin"></vs-icon>
    </button>
  </div>
</vs-dialog>
